/**
 * Shared industry list used across:
 *   - Admin > Company Settings
 *   - Admin > Org Hierarchy > Setup Wizard
 *   - Platform Admin > Create / Edit Tenant
 *   - ERS Opportunity Engine V3 (sector-based matching)
 *
 * Each entry has:
 *   value — stored in the database
 *   label — displayed in the UI
 *   sector — MSE-GIFT sector name(s) for opportunity matching (optional)
 */

export const INDUSTRIES = [
  // --- General / Cross-sector ---
  { value: 'general_engineering', label: 'General Engineering', sector: 'General Engineering' },
  { value: 'manufacturing',      label: 'Manufacturing', sector: 'Manufacturing' },
  { value: 'cross_sectoral',     label: 'Cross-Sectoral', sector: 'Cross-sectoral' },

  // --- Process Industries ---
  { value: 'chemicals',          label: 'Chemicals', sector: 'Chemical' },
  { value: 'pharmaceuticals',    label: 'Pharmaceuticals & Bulk Drugs', sector: 'Pharmaceutical & Bulk Drugs' },
  { value: 'food_beverage',      label: 'Food & Beverage', sector: 'Food Processing' },
  { value: 'food_processing',    label: 'Food Processing', sector: 'Food Processing' },
  { value: 'dairy',              label: 'Dairy', sector: 'Dairy' },
  { value: 'tea_processing',     label: 'Tea Processing', sector: 'Tea Processing' },
  { value: 'rubber',             label: 'Rubber', sector: 'Rubber' },
  { value: 'plastics_polymers',  label: 'Plastics & Polymers', sector: 'Plastics & Polymers' },
  { value: 'pulp_paper',         label: 'Pulp & Paper', sector: 'Pulp and Paper' },

  // --- Metals & Foundry ---
  { value: 'foundry',            label: 'Foundry', sector: 'Foundry' },
  { value: 'iron_steel',         label: 'Iron & Steel', sector: 'Iron & Steel' },
  { value: 'steel_rerolling',    label: 'Steel Re-Rolling', sector: 'Steel Re-rolling' },
  { value: 'mini_steel',         label: 'Mini Steel', sector: 'Mini Steel' },
  { value: 'brass_aluminium',    label: 'Brass & Aluminium', sector: 'Brass & Aluminium' },
  { value: 'aluminium_smelting', label: 'Aluminium Smelting', sector: 'Aluminium Smelting' },
  { value: 'forging',            label: 'Forging & Heat Treatment', sector: 'Forging and Heat Treatment' },

  // --- Building Materials ---
  { value: 'cement',             label: 'Cement', sector: 'Cement' },
  { value: 'ceramics',           label: 'Ceramics', sector: 'Ceramics' },
  { value: 'glass',              label: 'Glass', sector: 'Glass' },
  { value: 'bricks',             label: 'Bricks', sector: 'Bricks' },
  { value: 'sanitaryware',       label: 'Sanitaryware & Potteryware', sector: 'Sanitaryware & Potteryware' },

  // --- Textiles & Leather ---
  { value: 'textiles',           label: 'Textiles', sector: 'Textiles' },
  { value: 'leather',            label: 'Leather', sector: 'Leather' },

  // --- Engineering & Auto ---
  { value: 'auto_components',    label: 'Auto Components', sector: 'Auto components' },
  { value: 'machine_tool',       label: 'Machine Tool', sector: 'Machine Tool' },
  { value: 'hand_tools',         label: 'Hand Tools', sector: 'Hand Tools' },
  { value: 'wire_drawing',       label: 'Wire Drawing', sector: 'Wire Drawing' },
  { value: 'bicycle',            label: 'Bicycle', sector: 'Bicycle' },
  { value: 'sports_goods',       label: 'Sports Goods', sector: 'Sports Goods' },
  { value: 'electrical_equip',   label: 'Electrical Equipment Manufacturing', sector: 'Manufacturing of Electrical Equipments' },

  // --- Printing & Packaging ---
  { value: 'printing',           label: 'Printing', sector: 'Printing' },
  { value: 'packaging',          label: 'Packaging', sector: 'Packaging' },

  // --- Energy & Resources ---
  { value: 'oil_gas',            label: 'Oil & Gas', sector: 'Oil & Gas Sector' },
  { value: 'energy_utilities',   label: 'Energy & Utilities', sector: 'Power Plant' },
  { value: 'renewable_energy',   label: 'Renewable Energy', sector: 'Renewable Energy Technologies' },
  { value: 'refineries',         label: 'Refineries', sector: 'Refineries' },
  { value: 'chlor_alkali',       label: 'Chlor-Alkali', sector: 'Chlor-alkali' },
  { value: 'fertilizer',         label: 'Fertilizer', sector: 'Fertilizer' },

  // --- Construction & Buildings ---
  { value: 'construction',       label: 'Construction', sector: 'Construction' },
  { value: 'commercial_buildings', label: 'Commercial Buildings', sector: 'Commercial Buildings' },

  // --- Other ---
  { value: 'mining',             label: 'Mining', sector: 'Mining' },
  { value: 'stone_cutting',      label: 'Stone Cutting', sector: 'Stone Cutting' },
  { value: 'healthcare',         label: 'Healthcare', sector: 'Healthcare' },
  { value: 'logistics',          label: 'Logistics & Transportation', sector: 'Transportation' },
  { value: 'technology',         label: 'Technology', sector: 'Technology' },
  { value: 'scientific_instruments', label: 'Scientific Instruments', sector: 'Scientific Instruments' },
  { value: 'agricultural',       label: 'Agricultural Implements', sector: 'Agricultural Implements' },
  { value: 'ice_making',         label: 'Ice Making', sector: 'Ice Making' },
  { value: 'sponge_iron',        label: 'Sponge Iron', sector: 'Sponge Iron' },
  { value: 'other',              label: 'Other', sector: 'Others' },
];

/**
 * Resolve a raw industry string (free-text, snake_case, or label) to a
 * canonical `value` from the INDUSTRIES list.  Returns '' if no match.
 */
export function matchIndustry(raw) {
  if (!raw) return '';
  const lower = raw.toLowerCase().replace(/[\s_\-&/]+/g, ' ').trim();
  const match = INDUSTRIES.find((ind) => {
    if (ind.value === raw) return true;
    if (ind.label === raw) return true;
    const normValue = ind.value.replace(/_/g, ' ');
    const normLabel = ind.label.toLowerCase().replace(/[\s_\-&/]+/g, ' ').trim();
    return normValue === lower || normLabel === lower;
  });
  if (match) return match.value;
  // Partial / keyword fallback
  const partial = INDUSTRIES.find((ind) => {
    const normLabel = ind.label.toLowerCase();
    return normLabel.includes(lower) || lower.includes(normLabel);
  });
  return partial ? partial.value : '';
}

/**
 * Get the display label for an industry value.  Returns the raw value
 * if not found (graceful degradation for legacy data).
 */
export function industryLabel(value) {
  if (!value) return '';
  const found = INDUSTRIES.find((ind) => ind.value === value);
  return found ? found.label : value;
}

/**
 * Get the MSE-GIFT sector name(s) for an industry value.
 * Used by V3 opportunity matching to filter/boost sector-relevant items.
 */
export function industrySector(value) {
  if (!value) return '';
  const found = INDUSTRIES.find((ind) => ind.value === value);
  return found ? found.sector : '';
}
